<template>
  <div class="map-container">
    <!-- Información de distancias fija arriba del mapa -->
    <div class="distance-info-panel">
      <div class="distance-text">
        <span>📏 <b>Distancia en línea recta (Fórmula de Haversine):</b> {{ distance }} km</span>
        <br>
        <span>🛣️ <b>Distancia referencial por carretera:</b> 
          <span v-if="routeDistance === 'Calculando...'" class="route-loading">
            {{ routeDistance }} ⏳
          </span>
          <span v-else-if="routeDistance === 'N/D'" class="route-error">
            {{ routeDistance }} ❌
          </span>
          <span v-else class="route-success">
            {{ routeDistance }} km ✅
          </span>
        </span>
        <div v-if="routingError && routeDistance === 'N/D'" class="error-message">
          <small>⚠️ Error: {{ routingError }}</small>
        </div>
      </div>
    </div>

    <!-- Mapa en contenedor separado -->
    <div class="map-wrapper">
      <l-map ref="map" :zoom="zoom" :center="center" style="height: 400px; width: 100%;" @ready="onMapReady">
        <l-tile-layer :url="stadiaSatelliteUrl" :attribution="attribution" />

        <l-marker :lat-lng="sunshineExport" :icon="customIcon">
          <l-popup>SUNSHINE EXPORT S.A.C.</l-popup>
        </l-marker>

        <l-marker :lat-lng="dest" :icon="customIcon">
          <l-popup>Destino</l-popup>
        </l-marker>

        <l-polyline :lat-lngs="[sunshineExport, dest]" color="red" />
      </l-map>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup, LPolyline } from 'vue2-leaflet';
import L from 'leaflet';

export default {
  name: 'MapsSunExpert',
  components: { LMap, LTileLayer, LMarker, LPopup, LPolyline },
  props: {
    // Compatibilidad: arreglo con DMS [latDMS, lonDMS]
    destinationDMS: {
      type: Array,
      required: false,
      default: null
    },
    // Nueva opción: valores individuales que pueden ser número o string (DMS o decimal)
    destinationLat: { type: [Number, String], required: false, default: null },
    destinationLng: { type: [Number, String], required: false, default: null },
    // Alternativa: arreglo [lat, lng] en decimal o DMS
    destination: { type: Array, required: false, default: null },
    // Mostrar u ocultar la ruta por carretera
    showRoute: { type: Boolean, default: true }
  },
  data() {
    return {
      zoom: 11,
      sunshineExport: [-4.9005, -80.3314], // Piura base fija
      dest: [-4.9005, -80.3314],
      center: [-4.9005, -80.3314],
      distance: 0,
      routeDistance: null,
      routingError: null,
      rutaLayer: null,
      stadiaSatelliteUrl: 'https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg',
      attribution: '© Stadia Maps | © OpenStreetMap',
      customIcon: L.icon({
        iconUrl: 'https://cdn-icons-png.flaticon.com/256/684/684908.png',
        iconSize: [32, 32]
      })
    };
  },
  watch: {
    destinationDMS: { handler() { this.updateDestinationFromProps(); }, immediate: true },
    destinationLat: { handler() { this.updateDestinationFromProps(); }, immediate: true },
    destinationLng: { handler() { this.updateDestinationFromProps(); }, immediate: true },
    destination:   { handler() { this.updateDestinationFromProps(); }, immediate: true }
  },
  methods: {
    parseDMS(dms) {
      const regex = /(\d+)°\s*(\d+)?'\s*(\d+(?:\.\d+)?)?"?\s*([NSEW])?/i;
      const match = (typeof dms === 'string') ? dms.trim().match(regex) : null;
      if (!match) return NaN;
      const deg = parseFloat(match[1] || 0), min = parseFloat(match[2] || 0), sec = parseFloat(match[3] || 0);
      const dir = (match[4] || '').toUpperCase();
      let decimal = deg + min/60 + sec/3600;
      if (dir === 'S' || dir === 'W') decimal *= -1;
      return decimal;
    },
    parseAny(v){
      if (v == null || v === '') return NaN;
      if (typeof v === 'number') return v;
      const s = String(v).trim();
      if (s.includes('°') || /[NSEWnsew]/.test(s)) return this.parseDMS(s);
      const n = parseFloat(s);
      return isNaN(n) ? NaN : n;
    },
    updateDestinationFromProps(){
      
      let lat = null, lng = null;
      if (Array.isArray(this.destination) && this.destination.length === 2){
        lat = this.parseAny(this.destination[0]);
        lng = this.parseAny(this.destination[1]);
      } else if (this.destinationLat != null && this.destinationLng != null){
        lat = this.parseAny(this.destinationLat);
        lng = this.parseAny(this.destinationLng);
      } else if (Array.isArray(this.destinationDMS) && this.destinationDMS.length === 2){
        lat = this.parseAny(this.destinationDMS[0]);
        lng = this.parseAny(this.destinationDMS[1]);
      }
      
      if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)){
        this.dest = [lat, lng];
        this.center = [lat, lng];
        this.calculateDistance();
        this.calcularRutaCarretera();
      } else {
        console.warn('❌ Coordenadas inválidas:', { lat, lng });
      }
    },
    calculateDistance() {
      const [lat1, lon1] = this.sunshineExport;
      const [lat2, lon2] = this.dest;
      const R = 6371;
      const dLat = ((lat2 - lat1) * Math.PI) / 180;
      const dLon = ((lon2 - lon1) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      this.distance = (R * c).toFixed(2);
    },
    async calcularRutaCarretera() {
        if (!this.showRoute) return;
        
        // Verificar que las coordenadas sean válidas
        if (!this.dest || this.dest.length !== 2 || 
            this.dest[0] === this.sunshineExport[0] && this.dest[1] === this.sunshineExport[1]) {
          console.warn('⚠️ Coordenadas de destino inválidas o iguales al origen');
          this.routeDistance = 'N/D';
          return;
        }
        
        // Formatear coordenadas para OSRM (lng,lat)
        const origen = `${this.sunshineExport[1]},${this.sunshineExport[0]}`;
        const destino = `${this.dest[1]},${this.dest[0]}`;
        
        const endpoints = [
          // OSRM oficial
          {
            name: 'OSRM Official',
            url: `https://router.project-osrm.org/route/v1/driving/${origen};${destino}?overview=full&geometries=geojson&alternatives=false&steps=false`
          },
          // Fallback OSM community
          {
            name: 'OSM Routing',
            url: `https://routing.openstreetmap.de/routed-car/route/v1/driving/${origen};${destino}?overview=full&geometries=geojson&alternatives=false&steps=false`
          },
          // GraphHopper (alternativo)
          {
            name: 'GraphHopper',
            url: `https://graphhopper.com/api/1/route?point=${this.sunshineExport[0]},${this.sunshineExport[1]}&point=${this.dest[0]},${this.dest[1]}&vehicle=car&calc_points=true&type=json`
          }
        ];
        
        let success = false;
        this.routingError = null;
        
        for (const endpoint of endpoints) {
          try {
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 segundos timeout
            
            const response = await fetch(endpoint.url, { 
              method: 'GET',
              mode: 'cors',
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'SunExpert-Map/1.0'
              },
              signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            
            let route = null;
            let distance = null;
            let geometry = null;
            
            // Procesar respuesta según el endpoint
            if (endpoint.name === 'GraphHopper') {
              route = data.paths && data.paths[0];
              if (route) {
                distance = route.distance;
                geometry = route.points;
              }
            } else {
              // OSRM format
              route = data.routes && data.routes[0];
              if (route) {
                distance = route.distance;
                geometry = route.geometry;
              }
            }
            
            if (!route || !distance || !geometry) {
              throw new Error('Respuesta inválida: no se encontró ruta');
            }
            
            // Actualizar distancia
            this.routeDistance = (distance / 1000).toFixed(2);
            
            // Limpiar ruta anterior
            if (this.rutaLayer) {
              this.$refs.map.mapObject.removeLayer(this.rutaLayer);
              this.rutaLayer = null;
            }
            
            // Agregar nueva ruta al mapa
            if (endpoint.name === 'GraphHopper') {
              // GraphHopper usa un formato diferente
              const coordinates = this.decodePolyline(geometry);
              this.rutaLayer = L.polyline(coordinates, { 
                color: '#1565C0', 
                weight: 5, 
                dashArray: '5,5',
                opacity: 0.8
              }).addTo(this.$refs.map.mapObject);
            } else {
              // OSRM usa GeoJSON
              this.rutaLayer = L.geoJSON(geometry, { 
                style: { 
                  color: '#1565C0', 
                  weight: 5, 
                  dashArray: '5,5',
                  opacity: 0.8
                } 
              }).addTo(this.$refs.map.mapObject);
            }
            
            success = true;
            break;
            
          } catch (error) {
            console.error(`❌ Error con ${endpoint.name}:`, error);
            this.routingError = error.message;
            
            if (error.name === 'AbortError') {
              console.warn('⏱️ Timeout en la petición');
            }
            // Continuar con el siguiente endpoint
          }
        }
        
        if (!success) {
          console.error('💥 Todos los servicios de routing fallaron');
          this.routeDistance = 'N/D';
          
          // Limpiar ruta anterior en caso de error
          if (this.rutaLayer) {
            try { 
              this.$refs.map.mapObject.removeLayer(this.rutaLayer); 
            } catch(_) {}
            this.rutaLayer = null;
          }
          
          // Mostrar error en desarrollo
          if (process.env.NODE_ENV !== 'production') {
            console.warn('🚫 Error final de routing:', this.routingError);
          }
        }
    },
    // Método para decodificar polylines encoded (usado por GraphHopper)
    decodePolyline(encoded) {
      if (!encoded) return [];
      
      const poly = [];
      let index = 0;
      let lat = 0;
      let lng = 0;
      
      while (index < encoded.length) {
        let b, shift = 0, result = 0;
        do {
          b = encoded.charCodeAt(index++) - 63;
          result |= (b & 0x1f) << shift;
          shift += 5;
        } while (b >= 0x20);
        const deltaLat = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lat += deltaLat;
        
        shift = 0;
        result = 0;
        do {
          b = encoded.charCodeAt(index++) - 63;
          result |= (b & 0x1f) << shift;
          shift += 5;
        } while (b >= 0x20);
        const deltaLng = ((result & 1) ? ~(result >> 1) : (result >> 1));
        lng += deltaLng;
        
        poly.push([lat / 1e5, lng / 1e5]);
      }
      return poly;
    },
    onMapReady() {
      this.calcularRutaCarretera();
    }
  }
};
</script>

<style scoped>
@import "~leaflet/dist/leaflet.css";

.map-container {
  background: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.distance-info-panel {
  position: relative;
  z-index: 100;
  flex-shrink: 0; /* No se encoge */
}

.distance-text {
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  border: 1px solid #e0e0e0;
  position: relative;
  /* Asegurar que se mantenga por encima del mapa */
  z-index: 1001;
}

.map-wrapper {
  position: relative;
  z-index: 1;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* Asegurar que los controles de Leaflet no interfieran */
.map-wrapper :deep(.leaflet-container) {
  position: relative;
  z-index: 1;
}

.map-wrapper :deep(.leaflet-control-container) {
  position: relative;
  z-index: 2;
}

.route-loading {
  color: #ff9800;
  font-weight: bold;
}

.route-error {
  color: #f44336;
  font-weight: bold;
}

.route-success {
  color: #4caf50;
  font-weight: bold;
}

.error-message {
  margin-top: 8px;
  color: #f44336;
  font-size: 12px;
  padding: 5px 10px;
  background: #ffebee;
  border-radius: 4px;
  border-left: 3px solid #f44336;
}

/* Responsive */
@media (max-width: 768px) {
  .map-container {
    margin: 10px 0;
    padding: 8px;
  }
  
  .distance-text {
    padding: 12px;
    font-size: 14px;
  }
  
  .map-wrapper {
    height: 300px;
  }
}
</style>
